import { useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { useAppState } from "../context/StateContext";
import { Error } from "../models/error";
import { Notification, NotificationUpdateAction } from "../models/notification";
import { Filters, Searches, Sorts, Sort, Row } from "../models/shipeezi.table";
import NotificationsService from "../services/notifications.service";
import { checkError } from "../util/checkError";
import { NotificationAction } from "../util/enums";
import { handleSort } from "../util/handleSort";

interface NotificationsRow extends Row {
  read?: boolean;
}

interface UpdateProps {
  id: number;
  notification: Notification;
  action: NotificationAction;
}

export const useNotificationHook = () => {
  const { currentUser, setUnreadTotal } = useAppState();
  const { t: translate } = useTranslation();

  const [notifications, setNotifications] = useState<Notification[]>([]);

  const [notificationAction, setNotificationAction] = useState<
    NotificationUpdateAction | undefined
  >(undefined);

  const [total, setTotal] = useState<number>(0);
  const [importantTotal, setImportantTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [rows, setRows] = useState<NotificationsRow[]>([]);

  const getNotificationsList = () =>
    useQuery(
      ["notification.list", page, pageSize, filters, searches],
      () =>
        NotificationsService.getAllMy({
          page,
          pageSize,
          sortBy:
            sorting && sorting?.length > 0
              ? JSON.stringify(sorting)
              : JSON.stringify([["updatedAt", "desc"]]),
          include: JSON.stringify([{ association: "fromUser" }]),
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        }),
      {
        enabled: !!currentUser?.id,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) setNotifications(response.data);
        },
      }
    );

  const { refetch } = getNotificationsList();

  const getNotificationCount = () =>
    useQuery(
      ["notification.count", filters, searches],
      () =>
        NotificationsService.countMy({
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        }),
      {
        enabled: !!currentUser?.id,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          setTotal(response?.data || 0);
        },
      }
    );

  const getImportantNotificationCount = () =>
    useQuery(
      ["notification.count.important"],
      () =>
        NotificationsService.countMy({
          important: "1",
        }),
      {
        enabled: !!currentUser?.id,
        refetchOnWindowFocus: false,
        onSuccess: (response) => setImportantTotal(response?.data || 0),
      }
    );

  const { refetch: refetchImportantNotificationsCount } = getImportantNotificationCount();

  const getUnreadNotificationCount = () =>
    useQuery(
      ["notification.count.unread"],
      () =>
        NotificationsService.countMy({
          read: "0",
        }),
      {
        enabled: !!currentUser?.id,
        refetchOnWindowFocus: false,
        refetchInterval: 2 * 60 * 1000,
        onSuccess: (response) => {
          setUnreadTotal(response?.data || 0);
        },
      }
    );

  const { refetch: refetchUnreadNotificationsCount } = getUnreadNotificationCount();

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (values: Filters) => {
    setFilters(values);
    setPage(0);
  };

  const handleSorting = (sorts: Sort[]) => {
    setSorting(handleSort(sorts));
  };

  const handleCurrentPage = (page: number) => setPage(page - 1);

  const handleUpdateImportantOrReadInGrid = (
    notificationId: number,
    action: NotificationAction
  ) => {
    const findNotification = notifications.find(
      (notification) => Number(notification.id) === Number(notificationId)
    );

    if (filters && Object.keys(filters).length > 0) {
      refetch();

      return;
    }

    if (findNotification) {
      const notificationsData = notifications ? [...notifications] : [];
      const index = notificationsData.findIndex(
        (notification) => notification.id === notificationId
      );

      if (index !== -1 && notificationsData[index]) {
        switch (action) {
          case NotificationAction.READ:
            notificationsData[index].read = !notificationsData[index].read;
            break;
          case NotificationAction.ALL_READ:
            notificationsData[index].read = true;
            break;
          case NotificationAction.IMPORTANT:
            notificationsData[index].important = !notificationsData[index].important;
            break;
          default:
            return null;
        }
      }

      setNotifications([...notificationsData]);
    }
  };

  const handleOnSuccess = (id: number, action: NotificationAction) => {
    handleUpdateImportantOrReadInGrid(id, action);

    if (
      action === NotificationAction.READ ||
      action === NotificationAction.ALL_READ ||
      action === NotificationAction.READ_FROM_DROPDOWN ||
      action === NotificationAction.ALL_READ_FROM_DROPDOWN
    ) {
      refetchUnreadNotificationsCount();
    }

    if (
      action === NotificationAction.IMPORTANT ||
      action === NotificationAction.IMPORTANT_FROM_DROPDOWN
    ) {
      refetchImportantNotificationsCount();
    }
  };

  const useUpdateNotification = () => {
    return useMutation(
      ["notification.update"],
      ({ id, notification }: UpdateProps) => NotificationsService.update(id, notification),
      {
        onSuccess: (
          _,
          variables: { id: number; notification: Notification; action: NotificationAction }
        ) => {
          const { id, action } = variables;

          handleOnSuccess(id, action);
        },
        onError: (error: AxiosError<Error>) => {
          if (error && error.response) {
            checkError(error, translate("anErrorOccurredTryAgain"));
          }
        },
      }
    );
  };

  const useReadAllNotifications = () => {
    return useMutation(["notification.read.all"], () => NotificationsService.realAllMy(), {
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) {
          checkError(error, translate("anErrorOccurredTryAgain"));
        }
      },
    });
  };

  return {
    notifications,
    total,
    importantTotal,
    page,
    pageSize,
    setPageSize,
    rows,
    setRows,
    notificationAction,
    setNotificationAction,
    getNotificationCount,
    getNotificationsList,
    getImportantNotificationCount,
    getUnreadNotificationCount,
    refetchUnreadNotificationsCount,
    handleSearch,
    handleFilter,
    handleSorting,
    handleCurrentPage,

    useReadAllNotifications,
    useUpdateNotification,
  };
};
